// ---------------------------------------------------------------------------
// Point values, season epoch, and helper functions for the global points system.
// Points are prestige-only — never spent, never lost.
// ---------------------------------------------------------------------------

// ── Game win points (Unlimited & Generations share the same table) ──────────
export const UNLIMITED_POINTS = {
  1: 100,
  2: 75,
  3: 50,
  4: 35,
  5: 25,
  6: 15,
};

export const GENERATIONS_POINTS = UNLIMITED_POINTS; // same guess mechanic

// ── Riddled-In points (bucket-based) ────────────────────────────────────────
export const RIDDLED_IN_POINTS = [
  { max: 1, pts: 150 },
  { max: 2, pts: 100 },
  { max: 3, pts: 75 },
  { max: 5, pts: 50 },
  { max: 10, pts: 30 },
  { max: Infinity, pts: 15 },
];

// ── Universal values ────────────────────────────────────────────────────────
export const LOSS_POINTS = 5;             // participation points for any loss / give-up
export const DAILY_CHECKIN_POINTS = 15;
export const FIRST_GAME_OF_DAY_BONUS = 15;
export const POWERUP_USE_POINTS = 1;
export const POWERUP_POINTS_DAILY_CAP = 10; // max powerup-use points per day

// ── Streak milestone bonuses (on top of daily check-in) ─────────────────────
export const STREAK_MILESTONES = [
  { days: 3, bonus: 25 },
  { days: 7, bonus: 75 },
  { days: 14, bonus: 200 },
];

// ── Season calculation ──────────────────────────────────────────────────────
// Epoch: Monday 5 Jan 2026 00:00 UTC. Seasons are 14 days long.
const SEASON_EPOCH_MS = Date.UTC(2026, 0, 5); // Jan = 0
const SEASON_LENGTH_DAYS = 14;
const MS_PER_DAY = 86_400_000;

/**
 * Get the current season number (0-indexed from epoch).
 */
export const getCurrentSeasonId = () => {
  const now = Date.now();
  return Math.floor((now - SEASON_EPOCH_MS) / (SEASON_LENGTH_DAYS * MS_PER_DAY));
};

/**
 * Get the start and end dates for a given season number.
 * @param {number} seasonId
 * @returns {{ start: Date, end: Date }}
 */
export const getSeasonWindow = (seasonId) => {
  const startMs = SEASON_EPOCH_MS + seasonId * SEASON_LENGTH_DAYS * MS_PER_DAY;
  const endMs = startMs + (SEASON_LENGTH_DAYS - 1) * MS_PER_DAY;
  return { start: new Date(startMs), end: new Date(endMs) };
};

/**
 * Days remaining in the current season (inclusive of today).
 */
export const getDaysRemainingInSeason = () => {
  const seasonId = getCurrentSeasonId();
  const { end } = getSeasonWindow(seasonId);
  const diffMs = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / MS_PER_DAY));
};

/**
 * Calculate game-win points for Unlimited / Generations.
 * @param {number} guessesUsed
 * @param {boolean} won
 * @returns {number}
 */
export const getStandardGamePoints = (guessesUsed, won) => {
  if (!won) return LOSS_POINTS;
  return UNLIMITED_POINTS[guessesUsed] ?? LOSS_POINTS;
};

/**
 * Calculate game points for Riddled-In.
 * @param {number} guessesUsed
 * @param {boolean} won
 * @returns {number}
 */
export const getRiddledInPoints = (guessesUsed, won) => {
  if (!won) return LOSS_POINTS;
  const bucket = RIDDLED_IN_POINTS.find((b) => guessesUsed <= b.max);
  return bucket ? bucket.pts : LOSS_POINTS;
};

/**
 * Get the streak milestone bonus for a given streak length, if any.
 * @param {number} streak
 * @returns {number} bonus pts (0 if no milestone hit)
 */
export const getStreakBonus = (streak) => {
  const milestone = STREAK_MILESTONES.find((m) => m.days === streak);
  return milestone ? milestone.bonus : 0;
};
